import useTranslate from "../../../core/languages/hooks/useTranslate";
import React, { useEffect, useRef, useState } from "react";
import ClearDataButton from "./ClearDataButton";
import { BookingFilters } from "../../../booking/hooks/useBookingMeSearchData";
import { useEventMeSearchData } from "../../../booking/hooks/useEventMeSearchData";

interface Props {
    onChange: (updates: Partial<BookingFilters>) => void;
    events: { id: string | number; name: string }[];
}

const EventFilter: React.FC<Props> = ({ onChange, events = [] }) => {
    const t = useTranslate();
    const eventRef = useRef<HTMLDivElement | null>(null);
    const [showEventPopover, setShowEventPopover] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
    const { data: eventObjects, loading, fetchData } = useEventMeSearchData();

    const handleSelectEvent = (value: string | null) => {
        setSelectedEvent(value);
        setShowEventPopover(false);

        if (value) {
            onChange({ event: value });
        } else {
            onChange({ event: undefined });
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (eventRef.current && !eventRef.current.contains(e.target as Node)) {
            setShowEventPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!events || events.length === 0) return;

        fetchData({ event: events });
    }, [events, fetchData]);

    const renderPopoverOptions = (
        options: { label: string; value: string | number }[],
        onSelect: (value: string | null) => void
    ) =>
        options.map(opt => (
            <div
                key={String(opt.value)}
                className="flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                onClick={() => onSelect(String(opt.value))}
            >
                <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                    {opt.label}
                </span>
            </div>
        ));

    const optionsEvents = (eventObjects ?? []).map(e => ({
        label: e?.name ?? "Sin nombre",
        value: e?.id ?? "",
    }));

    return (
        <div
            ref={eventRef}
            className="relative w-60 cursor-pointer [ nc-hero-field-padding ] items-center min-w-0"
        >
            <div className="flex items-start">
                <div className="flex-grow min-w-0">
                    <div
                        className="w-full p-0 xl:text-lg font-semibold min-w-0 cursor-pointer text-neutral-800 dark:text-neutral-100"
                        onClick={() => setShowEventPopover(v => !v)}
                    >
                        {selectedEvent
                            ? (
                                <span className="whitespace-normal break-words block">
                                    {(eventObjects ?? []).find(e => String(e.id) === selectedEvent)?.name ?? t("bookingMeForm.event")}
                                </span>
                            )
                            : (
                                <span className="whitespace-normal break-words block text-neutral-800 dark:text-neutral-200">
                                    {t("bookingMeForm.event")}
                                </span>
                            )
                        }
                    </div>
                    <span className="block mt-0.5 text-sm text-neutral-400 font-light">
                        {t("bookingMeForm.searchByEvent")}
                    </span>
                </div>

                {selectedEvent && <div className="ml-2"><ClearDataButton onClick={() => handleSelectEvent(null)} /></div>}
            </div>

            {showEventPopover && !loading && (
                <div className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl max-h-60 overflow-y-auto">
                    {renderPopoverOptions(optionsEvents, handleSelectEvent)}
                </div>
            )}
        </div>
    );
};

export default EventFilter;
